import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Wand2,
  Loader2,
  Check,
  X,
  RefreshCw,
  CreditCard,
  Building2,
  Mail,
  Phone,
  Sparkles,
  AlertCircle,
  ListChecks,
  Search,
} from "lucide-react";
import { supabase } from "@/lib/supabase";

/* ---------- Types ---------- */
type ECardRow = {
  id: string;
  card_number: string | null;
  card_last4: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  email: string | null;
  taxicaller_account_id: string | null;
  status: string | null;
};

type CRMRow = {
  id: string;
  account_id: string;
  account_name: string;
  email: string | null;
  phone: string | null;
  traveller_full_name: string | null;
  default_cost_code: string | null;
  is_active: boolean | null;
};

type Reason = {
  kind: "exact_name" | "fuzzy_name" | "email" | "phone";
  detail: string;
  weight: number;
};

type Candidate = {
  account: CRMRow;
  score: number;
  reasons: Reason[];
};

type Suggestion = {
  ecard: ECardRow;
  bestCandidate: Candidate | null;
  alternatives: Candidate[]; // top 5 alts excluding the best
};

/* ---------- Helpers ---------- */
const norm = (s: string | null | undefined) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normEmail = (s: string | null | undefined) =>
  (s || "").toLowerCase().trim();

const digitsOnly = (s: string | null | undefined) =>
  (s || "").replace(/\D+/g, "");

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (!al) return bl;
  if (!bl) return al;
  const v0 = new Array(bl + 1).fill(0);
  const v1 = new Array(bl + 1).fill(0);
  for (let i = 0; i <= bl; i++) v0[i] = i;
  for (let i = 0; i < al; i++) {
    v1[0] = i + 1;
    for (let j = 0; j < bl; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      v1[j + 1] = Math.min(v1[j] + 1, v0[j + 1] + 1, v0[j] + cost);
    }
    for (let j = 0; j <= bl; j++) v0[j] = v1[j];
  }
  return v1[bl];
}

function similarity(a: string, b: string): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const dist = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);
  return 1 - dist / maxLen;
}

/** Score a single CRM candidate against an eCard. */
function scoreCandidate(ec: ECardRow, acct: CRMRow): Candidate | null {
  const reasons: Reason[] = [];
  let score = 0;

  const ecFull = norm(
    `${ec.customer_first_name || ""} ${ec.customer_last_name || ""}`
  );
  const acctName = norm(acct.account_name);
  const acctTraveller = norm(acct.traveller_full_name);

  // Exact name (full)
  if (
    ecFull &&
    (ecFull === acctName || ecFull === acctTraveller)
  ) {
    score += 60;
    reasons.push({
      kind: "exact_name",
      detail: `Exact name match: "${ec.customer_first_name || ""} ${ec.customer_last_name || ""}" ↔ "${acct.account_name}"`,
      weight: 60,
    });
  } else {
    // Fuzzy name (best of name vs traveller)
    const sim = Math.max(
      ecFull && acctName ? similarity(ecFull, acctName) : 0,
      ecFull && acctTraveller ? similarity(ecFull, acctTraveller) : 0
    );
    if (ecFull && sim >= 0.75) {
      const w = Math.round(sim * 45);
      score += w;
      reasons.push({
        kind: "fuzzy_name",
        detail: `Fuzzy name ${Math.round(sim * 100)}% similar`,
        weight: w,
      });
    }
  }

  // Email match
  const ecEmail = normEmail(ec.email);
  const acctEmail = normEmail(acct.email);
  if (ecEmail && acctEmail && ecEmail === acctEmail) {
    score += 35;
    reasons.push({
      kind: "email",
      detail: `Email match: ${ecEmail}`,
      weight: 35,
    });
  }

  // Phone match (digits-only, last 8)
  const acctPhone = digitsOnly(acct.phone);
  // eCards don't have phone — but if eCard has a customer name & there's
  // also a phone we could derive. For now, this is a placeholder kept
  // for future extension.
  void acctPhone;

  if (reasons.length === 0) return null;

  // Bonus if account is active
  if (acct.is_active) {
    score += 2;
  }

  return { account: acct, score, reasons };
}

const REASON_ICONS: Record<Reason["kind"], React.ReactNode> = {
  exact_name: <Sparkles className="w-3 h-3" />,
  fuzzy_name: <Sparkles className="w-3 h-3" />,
  email: <Mail className="w-3 h-3" />,
  phone: <Phone className="w-3 h-3" />,
};

const REASON_LABEL: Record<Reason["kind"], string> = {
  exact_name: "Exact Name",
  fuzzy_name: "Fuzzy Name",
  email: "Email",
  phone: "Phone",
};

/* ---------- Component ---------- */
const ECardCRMMatchWizard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [ecards, setEcards] = useState<ECardRow[]>([]);
  const [crmAccounts, setCrmAccounts] = useState<CRMRow[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [minScore, setMinScore] = useState<number>(35);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{
    suggestion: Suggestion;
    candidate: Candidate;
  } | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [eRes, cRes] = await Promise.all([
      supabase
        .from("ecards")
        .select(
          "id, card_number, card_last4, customer_first_name, customer_last_name, email, taxicaller_account_id, status"
        )
        .is("taxicaller_account_id", null)
        .order("card_last4", { ascending: true }),
      supabase
        .from("crm_accounts")
        .select(
          "id, account_id, account_name, email, phone, traveller_full_name, default_cost_code, is_active"
        )
        .order("account_name", { ascending: true })
        .limit(5000),
    ]);

    if (eRes.error) {
      toast({
        title: "Failed to load eCards",
        description: eRes.error.message,
        variant: "destructive",
      });
    }
    if (cRes.error) {
      toast({
        title: "Failed to load CRM accounts",
        description: cRes.error.message,
        variant: "destructive",
      });
    }

    setEcards((eRes.data || []) as ECardRow[]);
    setCrmAccounts((cRes.data || []) as CRMRow[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const runScan = useCallback(() => {
    setScanning(true);
    // Build email + name indexes for speed.
    const emailIdx: Record<string, CRMRow[]> = {};
    for (const a of crmAccounts) {
      const e = normEmail(a.email);
      if (!e) continue;
      if (!emailIdx[e]) emailIdx[e] = [];
      emailIdx[e].push(a);
    }

    const out: Suggestion[] = [];
    for (const ec of ecards) {
      // Skip cards with no useful identity at all
      const hasName =
        (ec.customer_first_name && ec.customer_first_name.trim()) ||
        (ec.customer_last_name && ec.customer_last_name.trim());
      const hasEmail = !!normEmail(ec.email);
      if (!hasName && !hasEmail) {
        out.push({ ecard: ec, bestCandidate: null, alternatives: [] });
        continue;
      }

      const scored: Candidate[] = [];
      // Email shortlist
      const emailHits = hasEmail ? emailIdx[normEmail(ec.email)] || [] : [];
      for (const a of emailHits) {
        const c = scoreCandidate(ec, a);
        if (c) scored.push(c);
      }
      // Name pass over the full set (limited)
      if (hasName) {
        const ecFull = norm(
          `${ec.customer_first_name || ""} ${ec.customer_last_name || ""}`
        );
        if (ecFull) {
          for (const a of crmAccounts) {
            const acctName = norm(a.account_name);
            const acctTraveller = norm(a.traveller_full_name);
            // Quick prefilter: share at least one word
            const ecTokens = new Set(ecFull.split(" "));
            const aTokens = new Set(
              `${acctName} ${acctTraveller}`.split(" ")
            );
            let shared = 0;
            for (const t of ecTokens) if (t.length >= 3 && aTokens.has(t)) shared++;
            if (shared === 0) continue;
            // Avoid double-scoring an email hit
            if (emailHits.some((eh) => eh.id === a.id)) continue;
            const c = scoreCandidate(ec, a);
            if (c) scored.push(c);
          }
        }
      }

      scored.sort((a, b) => b.score - a.score);
      const best = scored[0] || null;
      const alts = scored.slice(1, 6);
      out.push({
        ecard: ec,
        bestCandidate: best,
        alternatives: alts,
      });
    }
    setSuggestions(out);
    setScanning(false);
    toast({
      title: "Scan complete",
      description: `${out.filter((s) => s.bestCandidate).length} of ${out.length} unlinked cards have a candidate.`,
    });
  }, [ecards, crmAccounts]);

  const queue = useMemo(() => {
    return suggestions.filter(
      (s) =>
        !reviewedIds.has(s.ecard.id) &&
        s.bestCandidate &&
        s.bestCandidate.score >= minScore
    );
  }, [suggestions, reviewedIds, minScore]);

  const lowConfidence = useMemo(() => {
    return suggestions.filter(
      (s) =>
        !reviewedIds.has(s.ecard.id) &&
        s.bestCandidate &&
        s.bestCandidate.score < minScore
    );
  }, [suggestions, reviewedIds, minScore]);

  const noCandidate = useMemo(() => {
    return suggestions.filter(
      (s) => !reviewedIds.has(s.ecard.id) && !s.bestCandidate
    );
  }, [suggestions, reviewedIds]);

  const askConfirm = (s: Suggestion, c: Candidate) => {
    setConfirmTarget({ suggestion: s, candidate: c });
    setConfirmOpen(true);
  };

  const doConfirm = async () => {
    if (!confirmTarget) return;
    const { suggestion, candidate } = confirmTarget;
    setBusyId(suggestion.ecard.id);

    const { error: updErr } = await supabase
      .from("ecards")
      .update({ taxicaller_account_id: candidate.account.account_id })
      .eq("id", suggestion.ecard.id);

    if (updErr) {
      setBusyId(null);
      toast({
        title: "Link failed",
        description: updErr.message,
        variant: "destructive",
      });
      return;
    }

    const userRes = await supabase.auth.getUser();
    await supabase.from("ecard_match_audit").insert({
      ecard_id: suggestion.ecard.id,
      crm_account_id: candidate.account.account_id,
      match_score: candidate.score,
      match_reasons: candidate.reasons,
      action: "confirmed",
      performed_by: userRes.data.user?.id ?? null,
    });

    setReviewedIds((prev) => new Set(prev).add(suggestion.ecard.id));
    setBusyId(null);
    setConfirmOpen(false);
    setConfirmTarget(null);
    toast({
      title: "Linked",
      description: `****${suggestion.ecard.card_last4 || "----"} → ${candidate.account.account_name}`,
    });
  };

  const skip = async (s: Suggestion) => {
    if (!s.bestCandidate) {
      setReviewedIds((prev) => new Set(prev).add(s.ecard.id));
      return;
    }
    const userRes = await supabase.auth.getUser();
    await supabase.from("ecard_match_audit").insert({
      ecard_id: s.ecard.id,
      crm_account_id: s.bestCandidate.account.account_id,
      match_score: s.bestCandidate.score,
      match_reasons: s.bestCandidate.reasons,
      action: "skipped",
      performed_by: userRes.data.user?.id ?? null,
    });
    setReviewedIds((prev) => new Set(prev).add(s.ecard.id));
  };

  const stats = useMemo(() => {
    const total = suggestions.length;
    const withCandidate = suggestions.filter((s) => s.bestCandidate).length;
    const highConf = suggestions.filter(
      (s) => s.bestCandidate && s.bestCandidate.score >= 70
    ).length;
    const reviewed = reviewedIds.size;
    return { total, withCandidate, highConf, reviewed };
  }, [suggestions, reviewedIds]);

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            CRM ↔ eCard Auto-Match Wizard
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Scans every eCard with no <code>taxicaller_account_id</code> and
            scores candidate CRM accounts using exact name, fuzzy name
            (Levenshtein), and email matches. Confirm or skip each suggestion;
            confirmed links update <code>ecards.taxicaller_account_id</code>{" "}
            and are logged to <code>ecard_match_audit</code>.
          </div>

          <div className="flex flex-wrap gap-2 items-end">
            <Button
              onClick={runScan}
              disabled={loading || scanning || ecards.length === 0}
            >
              {scanning ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-1" />
              )}
              Scan {ecards.length} unlinked cards
            </Button>
            <Button variant="outline" onClick={() => loadAll()} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Reload
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Search className="w-3 h-3" />
                Min score
              </label>
              <Input
                type="number"
                min={0}
                max={150}
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value) || 0)}
                className="w-20 h-8"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatBlock label="Unlinked Cards" value={ecards.length} />
            <StatBlock label="With Candidate" value={stats.withCandidate} />
            <StatBlock
              label="High Confidence (≥70)"
              value={stats.highConf}
              tone="green"
            />
            <StatBlock label="Reviewed" value={stats.reviewed} tone="blue" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Loading data…
          </CardContent>
        </Card>
      ) : suggestions.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <ListChecks className="w-5 h-5 inline mr-2" />
            Click <b>Scan</b> to generate suggestions for unlinked eCards.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Review queue */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ListChecks className="w-4 h-4" />
                Review Queue
                <Badge variant="secondary">{queue.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {queue.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">
                  Nothing to review at score ≥ {minScore}.
                </div>
              ) : (
                <div className="space-y-3">
                  {queue.map((s) => (
                    <SuggestionRow
                      key={s.ecard.id}
                      suggestion={s}
                      busy={busyId === s.ecard.id}
                      onConfirm={(c) => askConfirm(s, c)}
                      onSkip={() => skip(s)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Low confidence */}
          {lowConfidence.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-amber-600">
                  <AlertCircle className="w-4 h-4" />
                  Low-Confidence Matches
                  <Badge variant="secondary">{lowConfidence.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {lowConfidence.slice(0, 50).map((s) => (
                    <SuggestionRow
                      key={s.ecard.id}
                      suggestion={s}
                      busy={busyId === s.ecard.id}
                      onConfirm={(c) => askConfirm(s, c)}
                      onSkip={() => skip(s)}
                      lowConfidence
                    />
                  ))}
                  {lowConfidence.length > 50 && (
                    <div className="text-xs text-muted-foreground italic">
                      … {lowConfidence.length - 50} more hidden. Confirm or skip
                      the visible ones first.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No candidate */}
          {noCandidate.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <X className="w-4 h-4" />
                  No Candidate Found
                  <Badge variant="secondary">{noCandidate.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-muted-foreground mb-2">
                  These cards lack identifying info or had no name/email overlap
                  with any CRM account. Link them manually from the Cards tab.
                </div>
                <div className="flex flex-wrap gap-2">
                  {noCandidate.slice(0, 100).map((s) => (
                    <Badge key={s.ecard.id} variant="outline" className="font-mono">
                      ****{s.ecard.card_last4 || "----"}
                      {s.ecard.customer_last_name
                        ? ` · ${s.ecard.customer_last_name}`
                        : ""}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Confirm modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Link</DialogTitle>
          </DialogHeader>
          {confirmTarget && (
            <div className="space-y-3 text-sm">
              <div className="bg-muted/40 rounded p-2">
                <div className="text-xs text-muted-foreground">eCard</div>
                <div className="flex items-center gap-2 font-medium">
                  <CreditCard className="w-3.5 h-3.5" />
                  ****{confirmTarget.suggestion.ecard.card_last4 || "----"}
                  <span className="text-muted-foreground">
                    {[
                      confirmTarget.suggestion.ecard.customer_first_name,
                      confirmTarget.suggestion.ecard.customer_last_name,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                </div>
              </div>
              <div className="bg-muted/40 rounded p-2">
                <div className="text-xs text-muted-foreground">CRM Account</div>
                <div className="flex items-center gap-2 font-medium">
                  <Building2 className="w-3.5 h-3.5" />
                  {confirmTarget.candidate.account.account_id} ·{" "}
                  {confirmTarget.candidate.account.account_name}
                </div>
              </div>
              <div className="text-xs">
                Score{" "}
                <Badge className="bg-emerald-600">
                  {confirmTarget.candidate.score}
                </Badge>{" "}
                · {confirmTarget.candidate.reasons.length} reason
                {confirmTarget.candidate.reasons.length === 1 ? "" : "s"}
              </div>
              <ul className="text-xs space-y-1 text-muted-foreground list-disc pl-5">
                {confirmTarget.candidate.reasons.map((r, i) => (
                  <li key={i}>
                    <b>{REASON_LABEL[r.kind]}</b> (+{r.weight}) — {r.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={!!busyId}
            >
              Cancel
            </Button>
            <Button onClick={doConfirm} disabled={!!busyId}>
              {busyId ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Confirm Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatBlock: React.FC<{
  label: string;
  value: number | string;
  tone?: "green" | "blue" | "amber";
}> = ({ label, value, tone }) => {
  const color =
    tone === "green"
      ? "text-emerald-600"
      : tone === "blue"
      ? "text-blue-600"
      : tone === "amber"
      ? "text-amber-600"
      : "text-foreground";
  return (
    <div className="border rounded-md p-2 bg-card">
      <div className="text-[11px] uppercase text-muted-foreground tracking-wide">
        {label}
      </div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
};

const SuggestionRow: React.FC<{
  suggestion: Suggestion;
  busy: boolean;
  onConfirm: (c: Candidate) => void;
  onSkip: () => void;
  lowConfidence?: boolean;
}> = ({ suggestion, busy, onConfirm, onSkip, lowConfidence }) => {
  const ec = suggestion.ecard;
  const ecName =
    [ec.customer_first_name, ec.customer_last_name].filter(Boolean).join(" ") ||
    "—";
  const best = suggestion.bestCandidate;

  return (
    <div className="border rounded-lg p-3 bg-card space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <CreditCard className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-sm">****{ec.card_last4 || "----"}</div>
            <div className="text-xs text-muted-foreground truncate">
              {ecName}
              {ec.email && (
                <span className="ml-2">
                  <Mail className="w-3 h-3 inline mr-0.5" />
                  {ec.email}
                </span>
              )}
            </div>
          </div>
        </div>
        {best && (
          <Badge
            className={
              lowConfidence
                ? "bg-amber-500"
                : best.score >= 70
                ? "bg-emerald-600"
                : "bg-blue-600"
            }
          >
            score {best.score}
          </Badge>
        )}
      </div>

      {best ? (
        <>
          <div className="border rounded-md p-2 bg-muted/30">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm">
                    <span className="font-mono">{best.account.account_id}</span>
                    <span className="text-muted-foreground"> · </span>
                    <span className="font-medium">{best.account.account_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {best.account.email && (
                      <>
                        <Mail className="w-3 h-3 inline mr-0.5" />
                        {best.account.email}
                      </>
                    )}
                    {best.account.phone && (
                      <span className="ml-2">
                        <Phone className="w-3 h-3 inline mr-0.5" />
                        {best.account.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  disabled={busy}
                  onClick={() => onConfirm(best)}
                >
                  {busy ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3 mr-1" />
                  )}
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-[11px]"
                  disabled={busy}
                  onClick={onSkip}
                >
                  <X className="w-3 h-3 mr-1" />
                  Skip
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {best.reasons.map((r, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className="text-[10px] flex items-center gap-1"
                  title={r.detail}
                >
                  {REASON_ICONS[r.kind]}
                  {REASON_LABEL[r.kind]} +{r.weight}
                </Badge>
              ))}
            </div>
          </div>

          {suggestion.alternatives.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">
                {suggestion.alternatives.length} other candidate
                {suggestion.alternatives.length === 1 ? "" : "s"}
              </summary>
              <div className="mt-2 space-y-1">
                {suggestion.alternatives.map((c) => (
                  <div
                    key={c.account.id}
                    className="flex flex-wrap items-center justify-between gap-2 border rounded p-1.5 bg-background"
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Building2 className="w-3 h-3 flex-shrink-0" />
                      <span className="font-mono">{c.account.account_id}</span>
                      <span className="truncate">· {c.account.account_name}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {c.score}
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[10px]"
                      disabled={busy}
                      onClick={() => onConfirm(c)}
                    >
                      Use this
                    </Button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          No candidate found.
        </div>
      )}
    </div>
  );
};

export default ECardCRMMatchWizard;
